import { useState } from "react";
import { Box, Grid, Stack, Typography } from "@material-ui/core";
import { AutoFixHigh as MakeoverIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import Label from "components/Label";
import type { REST_schema } from "../../../@types/sharedSchema";
import { MakeoverComparisonDialog, ComparisonPair } from "./MakeoverComparisonDialog";

type MinisiteAiMakeoversSectionProps = {
  images: REST_schema.PublicListingMakeoverImage[];
};

/**
 * "AI generated" section on a public minisite. Shows the AI makeover images as
 * tiles; clicking one opens a dedicated gallery modal (one image at a time, with
 * a before/both/after toggle). Downloads are disabled on public pages.
 */
export function MinisiteAiMakeoversSection({ images }: MinisiteAiMakeoversSectionProps): JSX.Element | null {
  const { t: translate } = useTranslation();
  const [dialog, setDialog] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  if (!images?.length) return null;

  const pairs: ComparisonPair[] = images.map((i) => ({ beforeUrl: i.sourceImageUrl, afterUrl: i.url }));

  return (
    <Stack spacing={1.5} sx={{ mt: { xs: 3, md: 1 } }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <MakeoverIcon color="primary" />
        <Typography variant="h5">{translate("makeovers.aiSectionTitle", "תמונות שעוצבו ב-AI")}</Typography>
        <Label color="primary">AI</Label>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {translate("makeovers.aiSectionSubtitle", "הדמיות עיצוב נאמנות למבנה הנכס. הקישו על תמונה לצפייה והשוואת לפני/אחרי.")}
      </Typography>

      <Grid container spacing={2}>
        {images.map((img, idx) => (
          <Grid item xs={6} sm={4} key={img.url}>
            <Box
              onClick={() => setDialog({ open: true, index: idx })}
              sx={{
                position: "relative",
                width: "100%",
                paddingTop: "75%",
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                boxShadow: 1,
                "&:hover": { boxShadow: 3 }
              }}
            >
              <Box
                component="img"
                src={img.url}
                alt=""
                loading="lazy"
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box sx={{ position: "absolute", top: 8, insetInlineStart: 8 }}>
                <Label color="primary">AI</Label>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <MakeoverComparisonDialog
        open={dialog.open}
        pairs={pairs}
        index={dialog.index}
        allowDownload={false}
        onClose={() => setDialog((d) => ({ ...d, open: false }))}
        onIndexChange={(index) => setDialog((d) => ({ ...d, index }))}
      />
    </Stack>
  );
}

export default MinisiteAiMakeoversSection;
